import { Component, computed, effect, input, output, signal, TemplateRef } from '@angular/core';
import { NgClass } from '@angular/common';
import { NgTemplateOutlet } from '@angular/common';

export interface ColumnDef<T> {
  key: string;
  header: string;
  sortKey?: string;
  cell?: (row: T) => string | number;
  cellTemplate?: TemplateRef<{ $implicit: T }>;
  headerClass?: string;
  cellClass?: string;
}

@Component({
  selector: 'app-data-table',
  imports: [NgTemplateOutlet, NgClass],
  templateUrl: './data-table.html',
  host: { class: 'flex flex-col h-full min-h-0' },
})
export class DataTableComponent<T> {
  columns = input.required<ColumnDef<T>[]>();
  rows = input.required<T[]>();
  pageSize = input<number>(10);
  total = input<number | null>(null);
  page = input<number>(1);
  clickableRows = input<boolean>(false);
  loading = input<boolean>(false);
  sortBy = input<string>('');
  sortDirection = input<'asc' | 'desc'>('asc');
  rowAction = output<T>();
  rowDelete = output<T>();
  pageChange = output<number>();
  sortChange = output<{ key: string; direction: 'asc' | 'desc' }>();

  protected currentPage = signal(1);

  protected effectivePage = computed(() =>
    this.total() !== null ? this.page() : this.currentPage(),
  );

  protected effectiveTotalPages = computed(() => {
    const t = this.total();
    return t !== null
      ? Math.max(1, Math.ceil(t / this.pageSize()))
      : Math.max(1, Math.ceil(this.rows().length / this.pageSize()));
  });

  protected visiblePages = computed(() => {
    const total = this.effectiveTotalPages();
    const current = this.effectivePage();
    const start = Math.max(1, Math.min(current - 1, total - 2));
    const end = Math.min(total, start + 2);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  protected paginatedRows = computed(() => {
    if (this.total() !== null) return this.rows();
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.rows().slice(start, start + this.pageSize());
  });

  constructor() {
    effect(() => {
      this.rows();
      this.currentPage.set(1);
    });
  }

  protected onSort(col: ColumnDef<T>): void {
    if (!col.sortKey) return;
    const dir = this.sortBy() === col.sortKey && this.sortDirection() === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit({ key: col.sortKey, direction: dir });
  }

  protected goToPage(page: number): void {
    const max = this.effectiveTotalPages();
    if (page < 1 || page > max) return;
    if (this.total() !== null) {
      this.pageChange.emit(page);
    } else {
      this.currentPage.set(page);
    }
  }
}
